import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useApp } from "../../contexts/AppContext";
import { toast } from "sonner";
import { Save, Image as ImageIcon } from "lucide-react";
import { ColorSelect, BUTTON_FORMATS, BUTTON_EFFECTS, paletteFromColor } from "../../components/ClayCard";

const FONTS = [
  { v: "fredoka", label: "Fredoka" }, { v: "nunito", label: "Nunito" }, { v: "outfit", label: "Outfit" },
  { v: "manrope", label: "Manrope" }, { v: "baloo", label: "Baloo 2" }, { v: "luckiest", label: "Luckiest Guy" },
];

const clamp = (v, min, max) => Math.min(max, Math.max(min, Number(v) || min));

const SAMPLE = {
  question: "O Pai Natal somou as idades dos seus dois elfos mais novos e obteve 12. Quantos anos passaram?",
  options: ["2 anos", "4 anos", "6 anos", "8 anos"],
};

function Slider({ label, value, min, max, step = 1, suffix, onChange, testId }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between text-sm font-semibold text-[#0B1D3A]">
        <span>{label}</span>
        <span className="tabular-nums text-[#005F73]">{value}{suffix}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full mt-2 accent-[#005F73]" data-testid={testId}
      />
      <div className="flex justify-between text-[10px] text-[#0B1D3A]/40 mt-0.5">
        <span>{min}{suffix}</span><span>{max}{suffix}</span>
      </div>
    </label>
  );
}

export default function AdminQuizAppearance() {
  const { settingsRaw: settings, refreshSettings } = useApp();
  const [imageWidth, setImageWidth] = useState(70);
  const [questionPx, setQuestionPx] = useState(22);
  const [answerPx, setAnswerPx] = useState(16);
  const [aspect, setAspect] = useState("auto");
  const [fit, setFit] = useState("contain");
  const [ansColor, setAnsColor] = useState("snow");
  const [ansFormat, setAnsFormat] = useState("soft");
  const [ansEffect, setAnsEffect] = useState("lift");
  const [ansFont, setAnsFont] = useState("fredoka");
  const [colL, setColL] = useState(2);
  const [colC, setColC] = useState(11);
  const [colR, setColR] = useState(5);
  const [sideVCenter, setSideVCenter] = useState(false);
  const [introEnabled, setIntroEnabled] = useState(true);
  const [introTitlePt, setIntroTitlePt] = useState("");
  const [introTitleEn, setIntroTitleEn] = useState("");
  const [introMsgPt, setIntroMsgPt] = useState("");
  const [introMsgEn, setIntroMsgEn] = useState("");
  const [introBtnPt, setIntroBtnPt] = useState("");
  const [introBtnEn, setIntroBtnEn] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setImageWidth(clamp(settings.quiz_image_width_pct ?? 70, 20, 100));
    setQuestionPx(clamp(settings.quiz_question_text_px ?? 22, 12, 48));
    setAnswerPx(clamp(settings.quiz_answer_text_px ?? 16, 12, 36));
    setAspect(settings.quiz_image_aspect || "auto");
    setFit(settings.quiz_image_fit === "cover" ? "cover" : "contain");
    setAnsColor(settings.quiz_answer_color || "snow");
    setAnsFormat(settings.quiz_answer_format || "soft");
    setAnsEffect(settings.quiz_answer_effect || "lift");
    setAnsFont(settings.quiz_answer_font || "fredoka");
    setColL(clamp(settings.quiz_col_left ?? 2, 1, 16));
    setColC(clamp(settings.quiz_col_center ?? 11, 1, 16));
    setColR(clamp(settings.quiz_col_right ?? 5, 1, 16));
    setSideVCenter(settings.quiz_side_vcenter === true || settings.quiz_side_vcenter === "true");
    setIntroEnabled(settings.quiz_intro_enabled !== false);
    setIntroTitlePt(settings.quiz_intro_title_pt || "");
    setIntroTitleEn(settings.quiz_intro_title_en || "");
    setIntroMsgPt(settings.quiz_intro_message_pt || "");
    setIntroMsgEn(settings.quiz_intro_message_en || "");
    setIntroBtnPt(settings.quiz_intro_button_pt || "");
    setIntroBtnEn(settings.quiz_intro_button_en || "");
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings_x", {
        quiz_image_width_pct: imageWidth,
        quiz_question_text_px: questionPx,
        quiz_answer_text_px: answerPx,
        quiz_image_aspect: aspect,
        quiz_image_fit: fit,
        quiz_answer_color: ansColor,
        quiz_answer_format: ansFormat,
        quiz_answer_effect: ansEffect,
        quiz_answer_font: ansFont,
        quiz_col_left: colL,
        quiz_col_center: colC,
        quiz_col_right: colR,
        quiz_side_vcenter: sideVCenter,
        quiz_intro_enabled: introEnabled,
        quiz_intro_title_pt: introTitlePt,
        quiz_intro_title_en: introTitleEn,
        quiz_intro_message_pt: introMsgPt,
        quiz_intro_message_en: introMsgEn,
        quiz_intro_button_pt: introBtnPt,
        quiz_intro_button_en: introBtnEn,
      });
      await refreshSettings();
      toast.success("Aparência do quiz guardada");
    } catch (e) {
      toast.error("Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const fixedAspect = aspect !== "auto";
  const previewAspect = fixedAspect ? aspect.replace("/", " / ") : "4 / 3";
  const ansPalette = paletteFromColor(ansColor);

  return (
    <div className="space-y-6" data-testid="admin-quiz-appearance">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-[#0B1D3A]">Aparência do Quiz</h1>
          <p className="text-sm text-[#0B1D3A]/55 mt-1">
            Tamanho da imagem, do texto da pergunta e dos botões de resposta no ecrã de jogo. Aplica-se a todos os quizzes.
          </p>
        </div>
        <button
          onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#005F73] hover:bg-[#0a4f5e] text-white font-bold text-sm transition-colors disabled:opacity-60 shrink-0"
          data-testid="quiz-appearance-save"
        >
          <Save size={16} /> {saving ? "A guardar..." : "Guardar"}
        </button>
      </div>

      {/* Intro window (pedagogical message + Start / 3-2-1 countdown) */}
      <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 space-y-4" data-testid="quiz-intro-settings">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-outfit font-bold text-lg text-[#0B1D3A]">Janela de introdução (antes de começar)</h2>
            <p className="text-xs text-[#0B1D3A]/55 mt-0.5">Mostra uma mensagem pedagógica + botão "Começar". Ao clicar, faz contagem 3‑2‑1 e inicia o tempo.</p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B1D3A]">
            <input type="checkbox" checked={introEnabled} onChange={(e) => setIntroEnabled(e.target.checked)} className="h-4 w-4 accent-[#1B5E3A]" data-testid="quiz-intro-enabled" />
            Ativar janela de introdução
          </label>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block text-sm">Título (PT)
            <input value={introTitlePt} onChange={(e) => setIntroTitlePt(e.target.value)} placeholder="Antes de começares" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="quiz-intro-title-pt" />
          </label>
          <label className="block text-sm">Title (EN)
            <input value={introTitleEn} onChange={(e) => setIntroTitleEn(e.target.value)} placeholder="Before you start" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" />
          </label>
          <label className="block text-sm">Mensagem (PT)
            <textarea value={introMsgPt} onChange={(e) => setIntroMsgPt(e.target.value)} rows="4" placeholder="(usa-se a mensagem predefinida se ficar vazio)" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="quiz-intro-message-pt" />
          </label>
          <label className="block text-sm">Message (EN)
            <textarea value={introMsgEn} onChange={(e) => setIntroMsgEn(e.target.value)} rows="4" placeholder="(default message used if left empty)" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" />
          </label>
          <label className="block text-sm">Botão (PT)
            <input value={introBtnPt} onChange={(e) => setIntroBtnPt(e.target.value)} placeholder="Começar" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" />
          </label>
          <label className="block text-sm">Button (EN)
            <input value={introBtnEn} onChange={(e) => setIntroBtnEn(e.target.value)} placeholder="Start" className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15" />
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        {/* Controls */}
        <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 space-y-6 h-fit">
          <Slider label="Tamanho da imagem" value={imageWidth} min={20} max={100} suffix="%"
            onChange={(v) => setImageWidth(clamp(v, 20, 100))} testId="slider-image-width" />

          <label className="block">
            <span className="text-sm font-semibold text-[#0B1D3A]">Proporção da janela da imagem</span>
            <select value={aspect} onChange={(e) => setAspect(e.target.value)}
              className="w-full mt-1.5 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" data-testid="select-image-aspect">
              <option value="auto">Automática (proporção original)</option>
              <option value="1/1">Quadrada (1:1)</option>
              <option value="4/3">Paisagem (4:3)</option>
              <option value="16/9">Panorâmica (16:9)</option>
              <option value="3/4">Retrato (3:4)</option>
            </select>
          </label>

          {fixedAspect && (
            <div data-testid="image-fit-wrap">
              <span className="text-sm font-semibold text-[#0B1D3A]">Ajuste dentro da janela</span>
              <div className="flex gap-2 mt-1.5">
                {[["contain", "Conter (sem cortar)"], ["cover", "Cobrir (preenche/corta)"]].map(([val, lbl]) => (
                  <button key={val} type="button" onClick={() => setFit(val)} data-testid={`fit-${val}`}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${fit === val ? "bg-[#005F73] text-white border-[#005F73]" : "bg-white text-[#0B1D3A] border-[#0B1D3A]/15 hover:border-[#005F73]"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
          )}

          <Slider label="Tamanho do texto da pergunta" value={questionPx} min={12} max={48} suffix="px"
            onChange={(v) => setQuestionPx(clamp(v, 12, 48))} testId="slider-question-px" />
          <Slider label="Tamanho do texto das respostas" value={answerPx} min={12} max={36} suffix="px"
            onChange={(v) => setAnswerPx(clamp(v, 12, 36))} testId="slider-answer-px" />

          <div className="pt-4 border-t border-[#0B1D3A]/10 space-y-3">
            <p className="text-sm font-bold text-[#0B1D3A]">Estilo dos botões de resposta</p>
            <label className="block">
              <span className="text-xs font-semibold text-[#0B1D3A]/70">Cor</span>
              <ColorSelect value={ansColor} onChange={setAnsColor} testId="ans-color"
                className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-[#0B1D3A]/70">Formato</span>
              <select value={ansFormat} onChange={(e) => setAnsFormat(e.target.value)} data-testid="ans-format"
                className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm">
                {BUTTON_FORMATS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-[#0B1D3A]/70">Efeito</span>
              <select value={ansEffect} onChange={(e) => setAnsEffect(e.target.value)} data-testid="ans-effect"
                className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm">
                {BUTTON_EFFECTS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-[#0B1D3A]/70">Tipo de letra</span>
              <select value={ansFont} onChange={(e) => setAnsFont(e.target.value)} data-testid="ans-font"
                className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 text-sm">
                {FONTS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
              </select>
            </label>
          </div>

          <div className="pt-4 border-t border-[#0B1D3A]/10 space-y-3" data-testid="quiz-layout-controls">
            <p className="text-sm font-bold text-[#0B1D3A]">Disposição das 3 áreas (ecrã de jogo)</p>
            <p className="text-xs text-[#0B1D3A]/50 -mt-1">Largura relativa de cada área. Por defeito 2 : 11 : 5.</p>
            <Slider label="Área 1 · Tempo e contagem" value={colL} min={1} max={16}
              onChange={(v) => setColL(clamp(v, 1, 16))} testId="slider-col-left" />
            <Slider label="Área 2 · Imagem" value={colC} min={1} max={16}
              onChange={(v) => setColC(clamp(v, 1, 16))} testId="slider-col-center" />
            <Slider label="Área 3 · Pergunta e respostas" value={colR} min={1} max={16}
              onChange={(v) => setColR(clamp(v, 1, 16))} testId="slider-col-right" />
            <p className="text-[11px] text-[#0B1D3A]/45 tabular-nums">Proporção atual: <b>{colL} : {colC} : {colR}</b></p>
            <button type="button" onClick={() => { setColL(2); setColC(11); setColR(5); }}
              className="text-xs font-semibold text-[#005F73] hover:underline" data-testid="reset-cols-btn">
              Repor proporção 2 : 11 : 5
            </button>
            <label className="flex items-center gap-2.5 text-sm font-semibold text-[#0B1D3A] cursor-pointer select-none pt-1" data-testid="side-vcenter-toggle">
              <input type="checkbox" checked={sideVCenter} onChange={(e) => setSideVCenter(e.target.checked)} className="h-4 w-4 accent-[#005F73]" />
              Centrar verticalmente as áreas 1 e 3
            </label>
          </div>

          <button
            className="text-xs font-semibold text-[#005F73] hover:underline" data-testid="quiz-appearance-reset"
          >
            Repor valores recomendados
          </button>
        </div>

        {/* Live preview */}
        <div className="rounded-2xl bg-[#FDFBF7] p-4 sm:p-6 border border-[#0B1D3A]/10 overflow-hidden" data-testid="quiz-appearance-preview">
          <div className="font-nunito text-xs font-bold text-[#0B1D3A]/40 mb-3 text-center uppercase tracking-wide">
            Pré-visualização
          </div>
          <div className="font-nunito text-sm font-bold text-[#0B1D3A]/50 mb-3 text-center">Pergunta 1 de 5</div>

          {/* Schematic of the 3-area layout (reflects proportions + vertical centering) */}
          <div className="mb-6" data-testid="layout-schematic">
            <p className="text-[11px] font-bold text-[#0B1D3A]/45 uppercase tracking-wide mb-1.5">Disposição das áreas</p>
            <div className="flex gap-2 h-28 rounded-xl bg-white border border-[#0B1D3A]/10 p-2">
              <div className={`flex flex-col ${sideVCenter ? "justify-center" : "justify-start"} items-center rounded-lg bg-[#005F73]/10 text-[#005F73] text-[10px] font-bold text-center p-1`} style={{ flexGrow: colL, flexBasis: 0 }} data-testid="schematic-area-1">
                <span>Tempo</span><span className="opacity-60">({colL})</span>
              </div>
              <div className="flex flex-col justify-center items-center rounded-lg bg-[#0B1D3A]/10 text-[#0B1D3A] text-[10px] font-bold text-center p-1" style={{ flexGrow: colC, flexBasis: 0 }} data-testid="schematic-area-2">
                <ImageIcon size={20} /><span className="opacity-60">Imagem ({colC})</span>
              </div>
              <div className={`flex flex-col ${sideVCenter ? "justify-center" : "justify-start"} items-center rounded-lg bg-[#2D9C5A]/12 text-[#1f6e40] text-[10px] font-bold text-center p-1`} style={{ flexGrow: colR, flexBasis: 0 }} data-testid="schematic-area-3">
                <span>Pergunta + respostas</span><span className="opacity-60">({colR})</span>
              </div>
            </div>
            <p className="text-[10px] text-[#0B1D3A]/40 mt-1">
              {sideVCenter ? "Áreas 1 e 3 centradas na vertical." : "Áreas 1 e 3 alinhadas ao topo."} Em ecrãs pequenos, as áreas empilham-se.
            </p>
          </div>

          <div className="mx-auto rounded-3xl overflow-hidden bg-white shadow-[0_12px_40px_-10px_rgba(11,29,58,0.15)] border border-[#0B1D3A]/5 mb-6 flex items-center justify-center"
            style={{ width: `${imageWidth}%`, maxWidth: "100%", aspectRatio: previewAspect }} data-testid="preview-image-box">
            <div className="flex flex-col items-center text-[#0B1D3A]/30">
              <ImageIcon size={48} />
              <span className="text-xs font-bold mt-2">Imagem da pergunta{fixedAspect ? ` · ${aspect.replace("/", ":")} · ${fit === "cover" ? "cobrir" : "conter"}` : " · proporção original"}</span>
            </div>
          </div>

          <h2 className="font-fredoka font-bold text-[#0B1D3A] mb-6 text-center leading-snug"
            style={{ fontSize: `${questionPx}px` }} data-testid="preview-question-text">
            {SAMPLE.question}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SAMPLE.options.map((opt, i) => {
              const correct = i === 1;
              const vars = correct
                ? { "--btn-bg": "#2D9C5A", "--btn-dark": "#1f6e40", "--btn-text": "#FFFFFF" }
                : { "--btn-bg": ansPalette.bg, "--btn-dark": ansPalette.dark, "--btn-text": ansPalette.text };
              return (
                <button key={i} type="button"
                  style={{ fontSize: `${answerPx}px`, ...vars }}
                  className={`home-btn fmt-${ansFormat} fx-${correct ? "none" : ansEffect} px-5 py-4 font-${ansFont} font-semibold`}
                  data-testid={`preview-answer-${i}`}
                >
                  <span className="relative z-[1] inline-flex h-8 w-8 items-center justify-center rounded-full font-bold mr-3 bg-white/25" style={{ color: "var(--btn-text)" }}>
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span className="relative z-[1]">{opt}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
